import { CheckoutUIConfigType } from '../types/merchantAttributes'
import { defaultTrustBadgesConfig } from 'src/assets/CustomizeUIConfig'

const parseBannerConfig = (data: any) =>
  data?.map((config: any) => ({
    enabled: config?.enabled,
    color: config?.background_color,
    text: config?.html_text_string,
    type: config?.type,
    textColor: config?.text_color
  }))

const parseCouponVisibilityConfig = (data: any) =>
  data?.map((config: any) => ({
    areCouponsVisible: config?.visibile,
    type: config?.type,
    visibilityExtent: config?.visibility_extent,
    hideSavings: config?.hide_savings
  }))

const parsePrimaryCTAConfigPayload = (data: any) =>
  data?.map((config: any) => ({ type: config?.type, CTAText: config?.cta_text }))

const parseUpSellConfig = (upsell: any) => ({
  isEnabled: upsell?.is_enabled,
  upSellTitle: upsell?.upsell_title,
  discountedUpSellTitle: upsell?.discounted_upsell_title
})

// Mirrors the old `attributes.ui` block from parseMerchantData, reading from the
// CHECKOUT ui_attributes root instead. Keep field-for-field parity.
export const parseCheckoutUIData = (ui: any): CheckoutUIConfigType => {
  return {
    isDiscountsEnabled: ui?.is_discounts_enabled ?? true,
    showBankOffers: ui?.discount_bank_offers ?? false,
    colorPalette: {
      primaryColor: ui?.color_pallet?.primary_color ?? '#000000',
      primaryTextColor: ui?.color_pallet?.primary_text_color ?? '#FFFFFF'
    },
    convenienceFeeConfig: ui?.convenience_fee_config,
    logoUrl: ui?.logo_url ?? '',
    bannerConfig: parseBannerConfig(ui?.banner_config) ?? [],
    couponVisibilityConfig: parseCouponVisibilityConfig(ui?.coupon_visibility_config) ?? [],
    primaryCTAConfig: parsePrimaryCTAConfigPayload(ui?.primary_cta_config) ?? [],
    trustBadgeConfig:
      Boolean(ui?.trust_badges) && JSON.stringify(ui?.trust_badges) !== '{}'
        ? ui?.trust_badges
        : defaultTrustBadgesConfig,
    showExitConfirmation: ui?.show_exit_confirmation ?? true,
    exitCheckoutAction: ui?.exit_checkout_action ?? 'none',
    exitNudge: {
      isEnabled: ui?.exit_checkout_action !== 'none' && Boolean(ui?.exit_checkout_action),
      selectedOption:
        ui?.exit_checkout_action === 'survey'
          ? 'SURVEY'
          : ui?.exit_checkout_action === 'nudge'
          ? 'EXIT_CONFIRMATION'
          : ui?.exit_checkout_action === 'exit_coupon_nudge'
          ? 'EXIT_COUPON_NUDGE'
          : ui?.exit_checkout_action === 'urgency_nudge'
          ? 'URGENCY_NUDGE'
          : 'SURVEY',
      exitCoupon: {
        selectedCoupon: ui?.exit_coupon_nudge_config?.selected_coupon ?? '',
        couponValue: ui?.exit_coupon_nudge_config?.coupon_value ?? '',
        additionalSurvey: Boolean(ui?.exit_coupon_nudge_config?.additional_survey_enabled),
        badgeBackground: ui?.exit_coupon_nudge_config?.badge?.bg_color ?? '#2C874A',
        badgeText: ui?.exit_coupon_nudge_config?.badge?.text_color ?? '#FFFFFF',
        badgeLabel: ui?.exit_coupon_nudge_config?.badge?.html_text_string ?? '',
        nudgeText: ui?.exit_coupon_nudge_config?.nudge?.text_color ?? '#27272A',
        nudgeMessage: ui?.exit_coupon_nudge_config?.nudge?.html_text_string ?? ''
      },
      urgency: {
        title: ui?.urgency_nudge_config?.title ?? '',
        textColor: ui?.urgency_nudge_config?.text_color ?? '#27272A',
        fomoMessage: ui?.urgency_nudge_config?.fomo_message ?? ''
      }
    },
    upSellConfig: parseUpSellConfig(ui?.upsell),
    isOrderSummaryOpen: Boolean(ui?.layout?.metadata?.is_order_summary_open),
    isQuantityEditable: Boolean(ui?.layout?.items_mutable),
    showOrderSummaryAs: ui?.layout?.order_summary_style ?? 'TILE',
    layout: {
      metadata: {
        is_order_summary_open: Boolean(ui?.layout?.metadata?.is_order_summary_open),
        show_original_price: Boolean(ui?.layout?.metadata?.show_original_price)
      },
      enable_marketing_flag: Boolean(ui?.layout?.enable_marketing_flag)
    },
    marketing_consent_text: ui?.marketing_consent_text ?? 'Keep me posted about sales and offers',
    delivery_config: {
      delivery_text: ui?.delivery_config?.delivery_text ?? '',
      enabled: Boolean(ui?.delivery_config?.enabled),
      is_etd_enabled: Boolean(ui?.delivery_config?.is_etd_enabled)
    },
    ...(ui?.dialog_display_config && {
      dialog_display_config: {
        dialogs: ui.dialog_display_config.dialogs ?? []
      }
    })
  }
}
